// @file composable for wish author state
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { blankUser } from '@@/bits/user_model'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceSimulatedAttributionStore } from '@@/pinia/surface_simulated_attribution_store'
import { useSurfaceUserContributorsStore } from '@@/pinia/surface_user_contributors'
import type { HashId, Id, User } from '@@/types'
import type { User as UserArvo } from '@padlet/arvo'
import type { ComputedRef } from 'vue'
import { computed, onMounted } from 'vue'

const RoleBadgeMap = {
  owner: __('owner'),
  admin: __('admin'),
  teacher: __('teacher'),
  student: __('student'),
}

function useAuthor({
  authorId,
  postOrCommentHashId,
}: {
  authorId: Id | HashId | undefined
  // Used in the demo padlet onboarding to simulate attribution
  postOrCommentHashId?: HashId
}): {
  authorRole: ComputedRef<string | null>
  xAuthorRole: ComputedRef<boolean>
  author: ComputedRef<UserArvo | User | undefined>
  username: ComputedRef<string | undefined>
} {
  const surfaceStore = useSurfaceStore()
  const surfaceUserContributorsStore = useSurfaceUserContributorsStore()
  const surfaceSimulatedAttributionStore = useSurfaceSimulatedAttributionStore()
  const { fetchUser, getUserById } = surfaceUserContributorsStore

  const author = computed<User>(() => {
    if (authorId == null) return blankUser()
    const user = getUserById(authorId)
    const result = user ?? blankUser()
    // Demo padlet onboarding
    // If the post or comment hash ID is provided, check if there is a simulated user for it then save that user's name and avatar
    if (postOrCommentHashId !== undefined) {
      const simulatedUser = surfaceSimulatedAttributionStore.attributions[postOrCommentHashId]
      if (simulatedUser != null) {
        return {
          ...result,
          name: simulatedUser.name,
          avatar: simulatedUser.avatar,
        }
      }
    }
    return result
  })

  const username = computed(() => author.value?.username)

  const authorRole = computed(() => {
    return xAuthorRole.value && author?.value.wall_role != null ? RoleBadgeMap[author.value.wall_role] : null
  })

  const xAuthorRole = computed<boolean>(() => {
    return isAppUsing('surfaceRoleBadges') && surfaceStore.isUsedInEducationalContext
  })

  onMounted((): void => {
    if (authorId == null) return
    if (getUserById(authorId) != null) return

    if (xAuthorRole.value) {
      void fetchUser(authorId, surfaceStore.wallId)
    } else {
      void fetchUser(authorId)
    }
  })

  return {
    author,
    username,
    authorRole,
    xAuthorRole,
  }
}

export { RoleBadgeMap, useAuthor }
